import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { PlansService } from './plans.service';

import { StateService } from '@uirouter/core';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { UnlockFeatureComponent } from './unlock-feature/unlock-feature.component';

@Injectable({
  providedIn: 'root',
})
export class CurrentPlanService {
  static readonly TRIAL_FEATURE_NAMES = [
    'monitoring',
    'alerts',
    'branding',
    'override',
    'display_control',
    'asset_library',
    'google_sheets',
    'social_media_posts',
    'social_media_followers',
    'subcompany_distribution',
    'shared_templates',
    'teacher_role',
    'classroom_alerts',
    'single_sign_on',
    'power_bi',
    'domo',
    'ems',
    'authenticated_web_pages',
    'screen_sharing',
    'screensaver',
    'publication_approval',
  ];
  currentPlan: any;

  constructor(
    private broadcaster: BroadcasterService,
    private stateService: StateService,
    private modalService: ModalService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private plansService: PlansService,
    private analyticsFactory: AnalyticsService,
  ) {
    this._loadCurrentPlan();

    broadcaster.on('risevision.company.selectedCompanyChanged', () => {
      this._loadCurrentPlan();
    });

    broadcaster.on('risevision.company.updated', () => {
      this._loadCurrentPlan();
    });
  }

  _loadCurrentPlan() {
    var company = this.companyStateService.getCopyOfSelectedCompany();
    var plan = null;

    if (company.id && company.parentPlanProductCode) {
      plan = _.cloneDeep(
        this.plansService.getPlan(company.parentPlanProductCode),
      );
      plan.isParentPlan = true;
      plan.status = 'Active';
    } else if (company.id && company.planProductCode) {
      plan = _.cloneDeep(this.plansService.getPlan(company.planProductCode));

      plan.status = company.planSubscriptionStatus;
      plan.trialPeriod = company.planTrialPeriod;
      plan.currentPeriodEndDate = new Date(company.planCurrentPeriodEndDate);
      plan.trialExpiryDate = new Date(company.planTrialExpiryDate);
    } else {
      plan = _.cloneDeep(this.plansService.getFreePlan());
    }

    this.currentPlan = plan;

    plan.subscriptionId = company.planSubscriptionId;
    plan.playerProTotalLicenseCount = company.playerProTotalLicenseCount;
    plan.playerProAvailableLicenseCount =
      company.playerProAvailableLicenseCount;
    plan.interactiveTotalLicenseCount = company.interactiveTotalLicenseCount;
    plan.interactiveAvailableLicenseCount =
      company.interactiveAvailableLicenseCount;
    plan.shareCompanyPlan = company.shareCompanyPlan;

    plan.billToId = company.planBillToId;
    plan.isPurchasedByParent =
      !!company.planBillToId &&
      !!company.planShipToId &&
      company.planBillToId !== company.planShipToId &&
      (this.isSubscribed() || this.isCancelledActive());
    plan.isAllocatedByParent =
      !!company.licenseAllocationActive &&
      !!company.allocatedFromCompanyId &&
      company.allocatedFromCompanyId !== company.id;
    plan.parentPlanCompanyName = company.parentPlanCompanyName;
    plan.parentPlanContactEmail = company.parentPlanContactEmail;

    console.debug('Current plan', plan);
    this.broadcaster.emitWithParams('risevision.plan.loaded', plan);
  }

  isPlanActive() {
    return this.isSubscribed() || this.isOnTrial() || this.isCancelledActive();
  }

  isFree() {
    return this.currentPlan.type === 'free';
  }

  get canModifyPlan(): boolean {
    return !(
      this.currentPlan.isParentPlan ||
      this.currentPlan.isPurchasedByParent ||
      this.currentPlan.isAllocatedByParent
    );
  }

  isParentPlan() {
    return !!this.currentPlan.isParentPlan;
  }

  isEnterpriseSubCompany() {
    return this.currentPlan.type === 'enterprisesub';
  }

  isSubscribed() {
    return !this.isFree() && this.currentPlan.status === 'Active';
  }

  isOnTrial() {
    return !this.isFree() && this.currentPlan.status === 'Trial';
  }

  isTrialExpired() {
    return !this.isFree() && this.currentPlan.status === 'Trial Expired';
  }

  isPaused() {
    return !this.isFree() && this.currentPlan.status === 'Paused';
  }

  isCancelled() {
    return !this.isFree() && this.currentPlan.status === 'Cancelled';
  }

  isCancelledActive() {
    var now = new Date();

    return this.isCancelled() && this.currentPlan.currentPeriodEndDate > now;
  }

  isUnlimitedPlan() {
    return this.plansService.isUnlimitedPlan(this.currentPlan);
  }

  // old plans factory
  showPurchaseOptions(displayCount?) {
    if (!this.userStateService.canAccessBilling()) {
      return;
    }

    this.stateService.go('apps.purchase.home', {
      displayCount: displayCount,
      subscriptionId: '',
    });
  }

  confirmAndPurchase(displayCount?) {
    if (!this.canModifyPlan || !this.userStateService.canAccessBilling()) {
      var contactInfo = this.currentPlan.parentPlanContactEmail
        ? ' at ' + this.currentPlan.parentPlanContactEmail
        : '';
      this.modalService.showMessage(
        'Almost there!',
        "There aren't available display licenses to assign to the selected displays. Contact your account administrator" +
          contactInfo +
          ' for additional display licenses.',
      );
    } else {
      this.modalService
        .confirm(
          'Almost there!',
          "There aren't available display licenses to assign to the selected displays. Subscribe to additional licenses?",
          'Yes',
          'No',
        )
        .then(() => {
          this.showPurchaseOptions(displayCount);
        });
    }
  }

  showUnlockThisFeatureModal() {
    this.analyticsFactory.track('free user popup seen', {
      source: 'share schedule button',
    });
    this.modalService
      .showDialog(UnlockFeatureComponent, null, {
        width: this.modalService.MODAL_SM,
      })
      .then(() => {
        this.showPurchaseOptions();
      });
  }

  initPlanTrial() {
    var plan = this.plansService.getUnlimitedPlan();

    var licenses = plan.proLicenseCount;
    var selectedCompany =
      this.companyStateService.getCopyOfSelectedCompany(true);
    var trialExpiry = new Date();
    trialExpiry.setDate(trialExpiry.getDate() + plan.trialPeriod);
    // Round down the date otherwise the subtraction may calculate an extra day.
    trialExpiry.setHours(trialExpiry.getHours() - 1);

    selectedCompany.planProductCode = plan.productCode;
    selectedCompany.planTrialPeriod = plan.trialPeriod + 1;
    selectedCompany.planTrialExpiryDate = trialExpiry;
    selectedCompany.planSubscriptionStatus = 'Trial';
    selectedCompany.featureSetName = 'default';
    selectedCompany.featureNames = CurrentPlanService.TRIAL_FEATURE_NAMES;
    selectedCompany.playerProTotalLicenseCount = licenses;
    selectedCompany.playerProAvailableLicenseCount = licenses;

    this.companyStateService.updateCompanySettings(selectedCompany);
  }
}
